import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../../api";
import "./Careers.css";
import { phoneInputProps, PhoneHint } from "../../utils/phone";

const blank = {
  applicant_name: "",
  applicant_email: "",
  applicant_phone: "",
  cover_letter: "",
  resume: null,
  application_letter: null,
  academic_documents: [],
  driver_documents: [],
};
const date = (value) =>
  value
    ? new Date(`${value}T00:00:00`).toLocaleDateString("en-TZ", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";
const deadline = (job) =>
  `${date(job.deadline)}${job.deadline_time ? ` at ${String(job.deadline_time).slice(0, 5)} EAT` : ""}`;
const words = (value) =>
  String(value || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

// Mirrors the server limits (SecureUploadService and PHP post_max_size) so applicants
// learn about an oversized file immediately instead of after a long upload.
const MB = 1024 * 1024;
const LIMITS = { resume: 5 * MB, document: 10 * MB, total: 60 * MB };
const formatMb = (bytes) => `${(bytes / MB).toFixed(1)} MB`;
const checkAttachmentSizes = (form) => {
  const files = [
    form.resume && { file: form.resume, max: LIMITS.resume },
    form.application_letter && { file: form.application_letter, max: LIMITS.document },
    ...form.academic_documents.map((file) => ({ file, max: LIMITS.document })),
    ...form.driver_documents.map((file) => ({ file, max: LIMITS.document })),
  ].filter(Boolean);
  // Some Android browsers ignore the input's accept list, so check the type before uploading.
  const wrongType = files.find(({ file }) => !/\.(pdf|docx?)$/i.test(file.name));
  if (wrongType) return `"${wrongType.file.name}" is not allowed. Upload PDF, DOC or DOCX files only. Photos (JPG/PNG) can be converted to PDF with a scanner app or "Print → Save as PDF".`;
  const tooLarge = files.find(({ file, max }) => file.size > max);
  if (tooLarge) return `"${tooLarge.file.name}" is ${formatMb(tooLarge.file.size)}. The maximum is ${formatMb(tooLarge.max).replace(".0", "")}; please compress it or scan at a lower resolution and try again.`;
  const total = files.reduce((sum, { file }) => sum + file.size, 0);
  if (total > LIMITS.total) return `All attachments together are ${formatMb(total)}. Keep the total under 60 MB by compressing large scans.`;
  return "";
};

const UploadLoader = ({ progress }) => {
  const percent = progress.total ? Math.min(100, Math.round((progress.loaded / progress.total) * 100)) : 0;
  const processing = progress.total > 0 && progress.loaded >= progress.total;
  return (
    <div className="upload-loader" role="status" aria-live="polite">
      <div className="upload-loader-card">
        <div className={`upload-loader-spinner ${processing ? "processing" : ""}`} aria-hidden="true" />
        <h3>{processing ? "Processing your application…" : "Uploading your documents…"}</h3>
        <p>{processing ? "Almost done. Your documents are being checked and saved." : "Please keep this page open until the upload finishes."}</p>
        <div className="upload-loader-bar" aria-hidden="true">
          <span className={processing ? "processing" : ""} style={{ width: `${processing ? 100 : percent}%` }} />
        </div>
        <div className="upload-loader-meta">
          <b>{processing ? "100%" : `${percent}%`}</b>
          {progress.total > 0 && <span>{formatMb(progress.loaded)} of {formatMb(progress.total)}</span>}
        </div>
      </div>
    </div>
  );
};

export default function Careers() {
  const { id } = useParams();
  const [brand, setBrand] = useState({
    organization_name: "Tanzania Revenue Workers Union",
    organization_short_name: "TAREWU",
    logo_url: "/TAREWU_logo-2.png",
  });
  const [jobs, setJobs] = useState([]),
    [selected, setSelected] = useState(null),
    [view, setView] = useState(id ? "job" : "jobs");
  const [form, setForm] = useState(blank),
    [receipt, setReceipt] = useState(null),
    [tracking, setTracking] = useState({ application_number: "", email: "" }),
    [tracked, setTracked] = useState(null);
  const [loading, setLoading] = useState(true),
    [sending, setSending] = useState(false),
    [uploadProgress, setUploadProgress] = useState(null),
    [message, setMessage] = useState("");
  // Warn before leaving the page while documents are still uploading.
  useEffect(() => {
    if (!uploadProgress) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [uploadProgress]);
  useEffect(() => {
    Promise.all([
      api.getBrandingSettings().catch(() => ({ data: null })),
      api.getPublicJobs(),
      id ? api.getPublicJob(id) : Promise.resolve(null),
    ])
      .then(([b, j, d]) => {
        if (b?.data) setBrand((x) => ({ ...x, ...b.data }));
        setJobs(Array.isArray(j?.data) ? j.data : []);
        if (d?.data) setSelected(d.data);
      })
      .catch((e) => setMessage(e.message))
      .finally(() => setLoading(false));
  }, [id]);
  const logo = useMemo(
    () =>
      brand.logo_url?.startsWith("http")
        ? brand.logo_url
        : `${api.baseUrl.replace(/\/api\/?$/, " ").trim()}${brand.logo_url}`,
    [brand.logo_url],
  );
  const showJob = (job) => {
    setSelected(job);
    setView("job");
    setMessage("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const apply = (job) => {
    setSelected(job);
    setForm(blank);
    setView("apply");
    setMessage("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const submit = async (e) => {
    e.preventDefault();
    setMessage("");
    const sizeProblem = checkAttachmentSizes(form);
    if (sizeProblem) {
      setMessage(sizeProblem);
      return;
    }
    setSending(true);
    const data = new FormData();
    [
      "applicant_name",
      "applicant_email",
      "applicant_phone",
      "cover_letter",
      "resume",
      "application_letter",
    ].forEach((k) => {
      if (form[k]) data.append(k, form[k]);
    });
    form.academic_documents.forEach((file) =>
      data.append("academic_documents[]", file),
    );
    form.driver_documents.forEach((file) =>
      data.append("driver_documents[]", file),
    );
    data.append("job_posting_id", selected.id);
    setUploadProgress({ loaded: 0, total: 0 });
    try {
      const r = await api.submitPublicApplication(data, (loaded, total) => setUploadProgress({ loaded, total }));
      setReceipt(r.data);
      setView("receipt");
    } catch (err) {
      setMessage(err.message);
    } finally {
      setSending(false);
      setUploadProgress(null);
    }
  };
  const track = async (e) => {
    e.preventDefault();
    setSending(true);
    setMessage("");
    setTracked(null);
    try {
      const r = await api.trackPublicApplication(
        tracking.application_number,
        tracking.email,
      );
      setTracked(r.data);
    } catch (err) {
      setMessage(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="careers-page">
      <header className="careers-header">
        <Link to="/careers" className="careers-brand">
          <img src={logo} alt="" />
          <span>
            <b>{brand.organization_short_name} CAREERS</b>
            <small>{brand.organization_name}</small>
          </span>
        </Link>
        <nav>
          <button onClick={() => setView("jobs")}>Vacancies</button>
          <button onClick={() => setView("track")}>Track application</button>
          <a href={brand.portal_url || "/login"}>Staff portal</a>
        </nav>
      </header>
      <main>
        {message && (
          <div className="careers-message">
            {message}
            <button onClick={() => setMessage("")}>×</button>
          </div>
        )}
        {loading ? (
          <div className="careers-loading">Loading careers portal…</div>
        ) : (
          <>
            {view === "jobs" && (
              <>
                <section className="careers-hero">
                  <span>WORK WITH TAREWU</span>
                  <h1>Build a stronger future for workers.</h1>
                  <p>
                    Explore current opportunities and submit your application
                    through the official TAREWU Careers Portal.
                  </p>
                  <button
                    onClick={() =>
                      document
                        .getElementById("vacancies")
                        ?.scrollIntoView({ behavior: "smooth" })
                    }
                  >
                    View open vacancies
                  </button>
                </section>
                <section className="vacancies" id="vacancies">
                  <div className="section-title">
                    <div>
                      <span>OPEN OPPORTUNITIES</span>
                      <h2>Current vacancies</h2>
                    </div>
                    <b>
                      {jobs.length} open position{jobs.length === 1 ? "" : "s"}
                    </b>
                  </div>
                  <div className="career-job-list">
                    {jobs.map((job) => (
                      <article key={job.id}>
                        <div className="job-mark">
                          {job.title.slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div className="job-top">
                            <span>{words(job.employment_type)}</span>
                            <small>Closes {deadline(job)}</small>
                          </div>
                          <h3>{job.title}</h3>
                          <p>
                            {job.department} · {job.location || "Tanzania"}
                          </p>
                        </div>
                        <button onClick={() => showJob(job)}>
                          View role <span>→</span>
                        </button>
                      </article>
                    ))}
                    {!jobs.length && (
                      <div className="career-empty">
                        <b>No vacancies are open right now.</b>
                        <p>Please check again later.</p>
                      </div>
                    )}
                  </div>
                </section>
              </>
            )}
            {view === "job" && selected && (
              <section className="job-detail">
                <button className="back" onClick={() => setView("jobs")}>
                  ← All vacancies
                </button>
                <div className="job-detail-head">
                  <div>
                    <span>
                      {selected.reference_number} ·{" "}
                      {words(selected.employment_type)}
                    </span>
                    <h1>{selected.title}</h1>
                    <p>
                      {selected.location || "Tanzania"} · Apply by{" "}
                      {deadline(selected)}
                    </p>
                  </div>
                  <button onClick={() => apply(selected)}>
                    Apply for this position
                  </button>
                </div>
                <div className="job-detail-body">
                  <article>
                    <h2>About the role</h2>
                    <p>{selected.description}</p>
                    {selected.responsibilities && (
                      <>
                        <h2>Responsibilities</h2>
                        <p>{selected.responsibilities}</p>
                      </>
                    )}
                    {selected.qualifications && (
                      <>
                        <h2>Qualifications and attributes</h2>
                        <p>{selected.qualifications}</p>
                      </>
                    )}
                    {selected.requirements && (
                      <>
                        <h2>General application conditions</h2>
                        <p>{selected.requirements}</p>
                      </>
                    )}
                  </article>
                  <aside>
                    <span>REFERENCE</span>
                    <b>{selected.reference_number}</b>
                    <span>DEPARTMENT</span>
                    <b>{selected.department}</b>
                    <span>REPORTS TO</span>
                    <b>{selected.reports_to}</b>
                    <span>NUMBER OF POSTS</span>
                    <b>{selected.number_of_posts}</b>
                    <span>EMPLOYMENT TYPE</span>
                    <b>{words(selected.employment_type)}</b>
                    <span>LOCATION</span>
                    <b>{selected.location || "Tanzania"}</b>
                    <span>DEADLINE</span>
                    <b>{deadline(selected)}</b>
                    {selected.salary_range && (
                      <>
                        <span>SALARY SCALE / STIPEND</span>
                        <b>{selected.salary_range}</b>
                      </>
                    )}
                  </aside>
                </div>
              </section>
            )}
            {view === "apply" && selected && (
              <section className="career-form-shell">
                <button className="back" onClick={() => setView("job")}>
                  ← Back to vacancy
                </button>
                <div className="career-form-heading">
                  <span>APPLICATION FORM</span>
                  <h1>{selected.title}</h1>
                  <p>
                    At this stage, submit only the documents needed for initial
                    assessment. Birth certificate or NIDA will be requested only
                    after shortlisting.
                  </p>
                </div>
                <form onSubmit={submit}>
                  <label>
                    Full name
                    <input
                      required
                      value={form.applicant_name}
                      onChange={(e) =>
                        setForm({ ...form, applicant_name: e.target.value })
                      }
                    />
                  </label>
                  <div className="form-pair">
                    <label>
                      Email address
                      <input
                        required
                        type="email"
                        value={form.applicant_email}
                        onChange={(e) =>
                          setForm({ ...form, applicant_email: e.target.value })
                        }
                      />
                    </label>
                    <label>
                      Phone number
                      <input
                        required
                        {...phoneInputProps}
                        value={form.applicant_phone}
                        onChange={(e) =>
                          setForm({ ...form, applicant_phone: e.target.value })
                        }
                      />
                      <PhoneHint value={form.applicant_phone} bilingual />
                    </label>
                  </div>
                  <label>
                    Brief cover note (optional)
                    <textarea
                      rows="5"
                      value={form.cover_letter}
                      onChange={(e) =>
                        setForm({ ...form, cover_letter: e.target.value })
                      }
                    />
                  </label>
                  <label className="cv-upload">
                    Signed application letter
                    <input
                      required
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) =>
                        setForm({
                          ...form,
                          application_letter: e.target.files?.[0] || null,
                        })
                      }
                    />
                    <small>
                      {form.application_letter?.name ||
                        "Attach signed application letter (PDF/DOC/DOCX, max 10 MB)"}
                    </small>
                  </label>
                  <label className="cv-upload">
                    Curriculum Vitae (CV)
                    <input
                      required
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) =>
                        setForm({
                          ...form,
                          resume: e.target.files?.[0] || null,
                        })
                      }
                    />
                    <small>
                      {form.resume?.name ||
                        "Attach your current CV with three referees (max 5 MB)"}
                    </small>
                  </label>
                  <label className="cv-upload academic-upload">
                    Academic and professional documents
                    <input
                      required
                      multiple
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) =>
                        setForm({
                          ...form,
                          academic_documents: Array.from(e.target.files || []),
                        })
                      }
                    />
                    {form.academic_documents.length ? (
                      <small className="documents-selected">
                        ✓ {form.academic_documents.length} document(s) selected
                      </small>
                    ) : (
                      <span className="document-privacy-note">
                        <i>i</i>
                        <span>
                          <b>Certificates and transcripts only</b>
                          <small>
                            Birth certificate or NIDA is not required at this
                            stage. Max 10 MB per file.
                          </small>
                        </span>
                      </span>
                    )}
                  </label>
                  {selected.title.toLowerCase() === "driver" && (
                    <label className="cv-upload driver-upload">
                      Driver documents
                      <input
                        required
                        multiple
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={(e) =>
                          setForm({
                            ...form,
                            driver_documents: Array.from(e.target.files || []),
                          })
                        }
                      />
                      {form.driver_documents.length ? (
                        <small className="documents-selected">
                          ✓ {form.driver_documents.length} driver document(s)
                          selected
                        </small>
                      ) : (
                        <span className="driver-document-note">
                          <i>!</i>
                          <span>
                            <b>Required for Driver applicants</b>
                            <small>
                              Attach your valid driving licence and Trade Test
                              Grade II certificate.
                            </small>
                          </span>
                        </span>
                      )}
                    </label>
                  )}
                  <label className="declaration">
                    <input required type="checkbox" /> I confirm that the
                    information submitted is true and complete.
                  </label>
                  <button className="submit-application" disabled={sending}>
                    {sending ? "Submitting…" : "Submit application"}
                  </button>
                </form>
                {uploadProgress && <UploadLoader progress={uploadProgress} />}
              </section>
            )}
            {view === "receipt" && receipt && (
              <section className="application-receipt">
                <div className="receipt-check">✓</div>
                <span>APPLICATION RECEIVED</span>
                <h1>Thank you for applying.</h1>
                <p>
                  Your application for <b>{receipt.job_title}</b> has been
                  received. Keep this private reference.
                </p>
                <div>
                  <small>APPLICATION REFERENCE</small>
                  <strong>{receipt.application_number}</strong>
                </div>
                <button
                  onClick={() => {
                    setTracking({
                      application_number: receipt.application_number,
                      email: form.applicant_email,
                    });
                    setView("track");
                  }}
                >
                  Track this application
                </button>
              </section>
            )}
            {view === "track" && (
              <section className="tracking-shell">
                <div className="tracking-intro">
                  <span>APPLICATION STATUS</span>
                  <h1>Track your application</h1>
                  <p>
                    Use your reference and the same email address used to apply.
                  </p>
                </div>
                <form onSubmit={track}>
                  <label>
                    Application reference
                    <input
                      required
                      value={tracking.application_number}
                      onChange={(e) =>
                        setTracking({
                          ...tracking,
                          application_number: e.target.value.toUpperCase(),
                        })
                      }
                      placeholder="TAR-CAR-2026-XXXXXX"
                    />
                  </label>
                  <label>
                    Email address
                    <input
                      required
                      type="email"
                      value={tracking.email}
                      onChange={(e) =>
                        setTracking({ ...tracking, email: e.target.value })
                      }
                    />
                  </label>
                  <button disabled={sending}>
                    {sending ? "Checking…" : "Check status"}
                  </button>
                </form>
                {tracked && (
                  <article className="tracking-result">
                    <div>
                      <span>{tracked.status.replaceAll("_", " ")}</span>
                      <small>{tracked.application_number}</small>
                    </div>
                    <h2>{tracked.job_title}</h2>
                    <p>
                      Applicant: <b>{tracked.applicant_name}</b>
                    </p>
                    <p>
                      Submitted:{" "}
                      {new Date(tracked.applied_date).toLocaleString("en-TZ")}
                    </p>
                    <div className="tracking-timeline">
                      {(tracked.timeline || []).map((step, index) => (
                        <div key={`${step.status}-${index}`}>
                          <i></i>
                          <span>
                            <b>{step.status.replaceAll("_", " ")}</b>
                            <small>
                              {new Date(step.changed_at).toLocaleDateString(
                                "en-TZ",
                              )}
                            </small>
                          </span>
                        </div>
                      ))}
                    </div>
                  </article>
                )}
              </section>
            )}
          </>
        )}
      </main>
      <footer className="careers-footer">
        <span>
          {brand.footer_text ||
            `© ${brand.organization_short_name}. All rights reserved.`}
        </span>
        <Link to="/login">Authorized staff sign in</Link>
      </footer>
    </div>
  );
}
